import { Bill } from '@enview/interface/types/bills/Bill';
import { ReactElement, useState } from 'react';
import { useDispatch } from 'react-redux';
import { PositionStatementPopover } from './PositionStatementPopover';
import CommentCheckIcon from './CommentCheckIcon';
import '../Popovers/Popover.scss';
import './BillPosition.scss';
import { trackBillPositionViewed } from '../../../analytics/BillAnalytics';
import { PopoverPosition } from 'react-tiny-popover';
import { g as abilityGlossary } from '../../../config/ability';
import { checkPermission } from '../../../dux';
import { t } from 'i18next';

type BillPositionButtonProps = {
  bill: Bill;
  showPlaceholderText?: boolean;
  positions?: PopoverPosition[];
  // 'pill' matches the dashed/filled pill styling used on the Bill Detail
  // header; table rows keep the default compact icon+text trigger.
  variant?: 'compact' | 'pill';
};

const BillPositionButton: React.FC<BillPositionButtonProps> = ({
  bill,
  showPlaceholderText,
  positions,
  variant = 'compact',
}) => {
  const dispatch = useDispatch();
  const [showPositionPopover, setShowPositionPopover] = useState(false);
  const positionStatement = bill.positionStatements && bill.positionStatements[0];
  const hasStatement = !!positionStatement && Object.keys(positionStatement).length > 0;

  const canEdit = !!dispatch(
    checkPermission(abilityGlossary.CREATE, abilityGlossary.BILL_POSITION, true),
  );

  const positionLabel = hasStatement
    ? t('bill.sidebar.positionStatement.viewPosition')
    : t('bill.sidebar.positionStatement.positionButton');

  const targetElement: ReactElement =
    variant === 'pill' ? (
      <div
        className={`bh-pill ${hasStatement ? 'bh-pill--filled' : 'bh-pill--dashed'}`}
        id={`position-button-${bill.id}`}
        title={hasStatement ? 'View Position Statement' : 'Add Position Statement'}
      >
        <CommentCheckIcon color={hasStatement ? '#F79728' : '#919191'} />
        <span>{positionLabel}</span>
      </div>
    ) : (
      <div
        className="d-flex bill-tag-button position-button-wrap"
        id={`position-button-${bill.id}`}
        style={{ marginRight: '12px' }}
        title={hasStatement ? 'View Position Statement' : 'Add Position Statement'}
      >
        <CommentCheckIcon color={hasStatement ? '#F79728' : '#919191'} />
        {showPlaceholderText && (
          <p className={`placeholder-text ${hasStatement ? 'text-dark' : ''}`}>
            {positionLabel}
          </p>
        )}
      </div>
    );

  const togglePopover = () => {
    if (!hasStatement && !canEdit) return;
    setShowPositionPopover(!showPositionPopover);
    if (showPositionPopover) {
      trackBillPositionViewed(bill.id);
    }
  };

  return (
    <PositionStatementPopover
      bill={bill}
      closePopup={() => setShowPositionPopover(false)}
      positions={positions}
      readOnly={!canEdit}
      show={showPositionPopover}
      target={targetElement}
      toggleCallback={togglePopover}
    />
  );
};

export default BillPositionButton;
